<script lang="ts" context="module">
  import { z } from 'zod';
  import { cva } from 'class-variance-authority';
  import { derived } from 'svelte/store';
  import { createQuery, type CreateQueryOptions } from '@tanstack/svelte-query';
  import type { GetMetaObjectsbyIdsQueryVariables } from '$gql/storefront/operations';

  enum METAOBJECT_KEYS {
    COLOR = 'color',
    VARIANT = 'variant'
  }

  const metafieldSchema = z.array(
    z.string().refine((s) => s.startsWith('gid://shopify/Metaobject/'))
  );
  const variantSchema = z.string().refine((s) => s.startsWith('gid://shopify/ProductVariant/'));
  const colorHEXSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/); // "#000000"

  /* GraphQL */ `
    query getMetaObjectsbyIds($ids: [ID!]!) {
      nodes(ids: $ids) {
        __typename
        ... on Metaobject {
          id
          fields {
            key
            value
          }
        }
      }
    }
  `;

  /* GraphQL */ `
    query getProductVariantsByIds($ids: [ID!]!) {
      nodes(ids: $ids) {
        __typename
        ... on ProductVariant {
          id
          title
        }
      }
    }
  `;

  const metaobjectQuery = derived(shopify, ($shopify) => {
    const getQuery = ({
      props,
      options
    }: {
      props: GetMetaObjectsbyIdsQueryVariables;
      options: CreateQueryOptions<
        {
          [k: string]: string;
        },
        unknown
      >;
    }) =>
      createQuery({
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
          const parsedMetaObject: Map<string, string> = new Map();
          const data: Map<string, string> = new Map();

          const metaobjectRes = await $shopify.getMetaObjectsbyIds({ ...props });
          metaobjectRes.nodes.forEach((value) => {
            if (value?.__typename === 'Metaobject') {
              let color: z.SafeParseReturnType<string, string> | undefined;
              let variant: z.SafeParseReturnType<string, string> | undefined;

              value.fields.forEach((field) => {
                if (field.key === METAOBJECT_KEYS.COLOR && field.value) {
                  color = colorHEXSchema.safeParse(field.value);
                }
                if (field.key === METAOBJECT_KEYS.VARIANT && field.value) {
                  variant = variantSchema.safeParse(field.value);
                }
              });

              // Update Map if both keys present and successfully parsed (Variant ID + Color)
              if (variant?.success && color?.success) {
                parsedMetaObject.set(variant.data, color.data);
              }
            }
          });

          // Get parsed Variants by ID
          const variantsRes = await $shopify.getProductVariantsByIds({
            ids: [...parsedMetaObject.keys()]
          });
          variantsRes.nodes.forEach((variant) => {
            if (variant?.__typename === 'ProductVariant') {
              const color = parsedMetaObject.get(variant.id);
              if (color) {
                data.set(variant.title, color);
              }
            }
          });

          return Object.fromEntries(data.entries());
        },
        ...options
      });

    return { getQuery };
  });

  const host = cva(['flex'], {
    variants: {
      type: {
        card: [
          'bg-highlight/80',
          'shadow-none',
          'outline-none',
          'border-none',
          'relative',
          'isolate',
          'rounded-full',
          'w-6',
          'lg:w-7',
          'overflow-y-auto',
          'hide-scrollbar',
          'motion-safe:transition-all',
          '!duration-300',
          'z-20',
          'p-1',
          'gap-1',
          'flex-col-reverse'
        ],
        pdp: ['gap-3', 'flex-wrap', 'pl-0.5']
      },
      open: {
        true: 'w-10 lg:w-7 lg:p-1 lg:gap-1 p-2 gap-2',
        false: ''
      }
    }
  });
  const cell = cva(
    [
      'aspect-square',
      'disabled:pointer-events-none',
      'disabled:select-none',
      'hover:opacity-90',
      'shadow-none',
      'outline-none',
      'border-none',
      'focus-visible:ring shrink-0',
      'motion-safe:transition-all',
      '!duration-300',
      'rounded-full',
      'bg-[--bg]',
      'relative'
    ],
    {
      variants: {
        type: {
          card: ['w-full', 'isolate', '-z-[var(--i)]'],
          pdp: [
            'w-9',
            'h-9',
            'after:content-[""]',
            'after:absolute',
            'after:top-[-2px]',
            'after:left-[-2px]',
            'after:w-10',
            'after:h-10',
            'after:border',
            'after:border-primary/14',
            'after:rounded-full'
          ]
        },
        open: { true: '', false: '' },
        first: { true: '', false: '-mb-[calc(100%+1px)]' },
        hidden: {
          true: 'invisible motion-safe:[transition:margin_0.3s,visibility_0.2s] -mb-[calc(100%+4px)]',
          false: ''
        },
        selected: {
          true: 'after:border-accent',
          false: ''
        }
      },
      compoundVariants: [
        { open: true, first: false, class: 'mb-0' },
        { open: true, hidden: true, class: 'visible' }
      ]
    }
  );
</script>

<script lang="ts">
  import Skeleton from './ui/skeleton.svelte';

  import { cn } from '$lib/utils';
  import { shopify } from '$lib/shopify';
  import { focusTrap } from '$lib/actions/focus-trap';
  import { crossfade } from 'svelte/transition';
  import { objectEntries } from '$lib/utils/object';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { aboveLg } from '$lib/stores/breakpoints';
  import { createDebouncedValue } from '$lib/stores/debounced';

  let className: string | undefined = undefined;
  export { className as class };
  export let metafield: string;
  export let selected: string | null | undefined = undefined;
  export let selectorVariant: 'card' | 'pdp' = 'card';
  export let selectedOptions: Record<string, string> | undefined = undefined;

  $: optionName = Object.keys(selectedOptions || {})[0];

  $: {
    if (!selected) selected = Object.values(selectedOptions || {}).join(' / ');
  }

  let timeout: NodeJS.Timeout | null = null;
  let hasInteracted = false;

  const [open, deboucedOpen] = createDebouncedValue(false, 100);

  $: if ($open && hasInteracted && !$aboveLg) {
    if (timeout) {
      clearTimeout(timeout);
      hasInteracted = false;
    }
    timeout = setTimeout(() => {
      open.set(false);
      hasInteracted = false;
    }, 1000);
  }

  $: ids = JSON.parse(metafield);
  $: enabled = metafieldSchema.safeParse(ids).success;
  $: query = $metaobjectQuery.getQuery({
    props: { ids },
    options: { enabled, queryKey: ['product-card-variant-color', metafield] }
  });

  const [send, receive] = crossfade({});

  const handleSelect = (value: string) => {
    if (selectedOptions) {
      selectedOptions[optionName] = value;
    }
  };
</script>

{#if enabled && $query.data && !!objectEntries($query.data).length}
  {@const entries =
    $open || selectorVariant === 'pdp'
      ? objectEntries($query.data)
      : objectEntries($query.data).sort(([a, _]) => (a.toString() === selected ? -1 : 0))}

  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class={cn(
      host({ type: selectorVariant, open: selectorVariant === 'card' && $open }),
      className
    )}
    on:mouseenter={() => selectorVariant === 'card' && open.set(true)}
    on:mouseleave={() => selectorVariant === 'card' && open.set(false)}
    on:keydown={(e) => {
      if (selectorVariant === 'card' && e.key === 'Escape') {
        open.set(false);
      }
    }}
    on:click={(e) => {
      e.preventDefault();
      selectorVariant === 'card' && !$deboucedOpen && open.set(true);
    }}
    use:focusTrap={{ enabled: $open }}
  >
    {#if selectorVariant === 'card'}
      <button
        aria-label="Color Selector Open"
        type="button"
        class="inset-0 outline-none absolute z-20 disabled:pointer-events-none"
        on:click={(e) => {
          e.preventDefault();
          open.set(true);
        }}
        disabled={$open}
      />
    {/if}

    {#each entries as [variant, color], i}
      <!-- svelte-ignore a11y-mouse-events-have-key-events -->
      <button
        aria-label={`Color Selector ${variant}`}
        type="button"
        disabled={selectorVariant === 'card' && !$deboucedOpen}
        on:mouseover={() => selectorVariant === 'card' && (selected = `${variant}`)}
        on:click={(e) => {
          e.preventDefault();
          selected = `${variant}`;
          handleSelect(`${variant}`);
          hasInteracted = true;
          if ($aboveLg && selectorVariant === 'card') open.set(false);
        }}
        style:--bg={color}
        style:--i={i}
        class={cn(
          cell({
            type: selectorVariant,
            open: selectorVariant === 'card' && $open,
            first: selectorVariant === 'pdp' || i === 0,
            hidden: selectorVariant === 'card' && i > 2,
            selected: selected === `${variant}`
          })
        )}
      >
        {#if selectorVariant === 'card' && selected === `${variant}`}
          <div
            class={cn(
              'w-2 h-2 lg:w-1.5 lg:h-1.5 aspect-square rounded-full motion-safe:transition-all opacity-100 z-10 m-auto absolute inset-0 bg-background/88',
              !$open && 'opacity-0'
            )}
            in:send={{ key: 'indicator', duration: $reducedMotion ? 0 : 400 }}
            out:receive={{ key: 'indicator', duration: $reducedMotion ? 0 : 400 }}
          />
        {/if}
      </button>
    {/each}
  </div>
{:else if $query.isLoading}
  <Skeleton
    class={cn(
      selectorVariant === 'card' ? 'w-6 h-[30px] lg:w-7 lg:h-[34px] rounded-full' : 'w-full h-10',
      className
    )}
  />
{/if}
